import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Cog,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle2,
  CalendarPlus,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Wrench,
  User,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  scheduled: { label: "Scheduled", color: "bg-blue-200 text-blue-800" },
  pending: { label: "Pending", color: "bg-amber-200 text-amber-800" },
  confirmed: { label: "Confirmed", color: "bg-emerald-200 text-emerald-800" },
  overdue: { label: "Overdue", color: "bg-red-200 text-red-800" },
  completed: { label: "Completed", color: "bg-slate-300 text-slate-700" },
  cancelled: { label: "Cancelled", color: "bg-slate-200 text-slate-500" },
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [installations, setInstallations] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState("compact"); // "compact" or "full"
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [profileRes, installationsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/my/profile`, { withCredentials: true }),
        axios.get(`${API}/my/installations`, { withCredentials: true }),
        axios.get(`${API}/my/bookings`, { withCredentials: true }),
      ]);
      setProfile(profileRes.data);
      setInstallations(installationsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load your data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getServiceStatus = (installation) => {
    if (!installation.next_service_due) {
      return { label: "No Service Data", color: "bg-slate-100 text-slate-600", icon: Clock };
    }
    const today = new Date();
    const dueDate = new Date(installation.next_service_due);
    const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

    if (daysUntilDue < 0) {
      return { label: "Overdue", color: "bg-red-100 text-red-700", icon: AlertTriangle };
    }
    if (daysUntilDue <= 14) {
      return { label: "Due Soon", color: "bg-amber-100 text-amber-700", icon: Clock };
    }
    return { label: "Up to Date", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 };
  };

  const upcomingBookings = bookings.filter(
    (b) => b.status === "pending" || b.status === "confirmed"
  );

  const needsService = installations.filter((inst) => {
    if (!inst.next_service_due) return false;
    const today = new Date();
    const dueDate = new Date(inst.next_service_due);
    const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilDue <= 14;
  });

  // Calendar view bookings - include scheduled, pending, confirmed
  const calendarBookings = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    return bookings
      .filter((b) => b.booking_date >= today && ["scheduled", "pending", "confirmed"].includes(b.status))
      .sort((a, b) => a.booking_date.localeCompare(b.booking_date) || 
        (a.time_slot?.start_time || "").localeCompare(b.time_slot?.start_time || ""));
  }, [bookings]);

  // Calendar helpers
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getBookingsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return calendarBookings.filter((b) => b.booking_date === dateStr);
  };

  const navigateMonth = (delta) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + delta);
      return newDate;
    });
  };

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const getBookingColor = (status) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "confirmed":
        return "bg-emerald-100 text-emerald-700 border-emerald-200";
      case "pending":
        return "bg-amber-100 text-amber-700 border-amber-200";
      default:
        return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="h-32 bg-slate-200 rounded-sm"></div>
            <div className="h-32 bg-slate-200 rounded-sm"></div>
            <div className="h-32 bg-slate-200 rounded-sm"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="customer-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
          Welcome back{profile?.name ? `, ${profile.name.split(" ")[0]}` : ""}
        </h1>
        <p className="text-slate-500 mt-1">
          Manage your installations and service bookings
        </p>
      </div>

      {/* My Service Calendar - Prominent at top */}
      <Card className="rounded-sm border-slate-200 mb-8" data-testid="customer-calendar">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-indigo-600" />
              <CardTitle className="text-lg font-semibold">My Service Calendar</CardTitle>
              <Badge variant="outline" className="ml-2 text-xs">
                {calendarBookings.length} upcoming
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={calendarView === "compact" ? "default" : "outline"}
                size="sm"
                onClick={() => setCalendarView("compact")}
                className="rounded-sm"
              >
                List
              </Button>
              <Button
                variant={calendarView === "full" ? "default" : "outline"}
                size="sm"
                onClick={() => setCalendarView("full")}
                className="rounded-sm"
              >
                Calendar
              </Button>
              <Button
                onClick={() => navigate("/book-service")}
                size="sm"
                className="rounded-sm bg-indigo-600 hover:bg-indigo-700"
                data-testid="calendar-book-service-btn"
              >
                <CalendarPlus className="h-4 w-4 mr-2" />
                Book Service
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {calendarView === "compact" ? (
            /* Compact View - Upcoming Bookings List */
            <div className="space-y-3">
              {calendarBookings.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No upcoming bookings</p>
                  <p className="text-sm mt-1">Schedule a service to see it here</p>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/book-service")}
                    className="mt-4 rounded-sm"
                  >
                    <CalendarPlus className="h-4 w-4 mr-2" />
                    Book a Service
                  </Button>
                </div>
              ) : (
                calendarBookings.slice(0, 5).map((booking) => (
                  <div
                    key={booking.booking_id}
                    className={`flex items-center justify-between p-4 border rounded-sm hover:shadow-sm transition-shadow cursor-pointer ${getBookingColor(booking.status)}`}
                    data-testid={`calendar-booking-${booking.booking_id}`}
                    onClick={() => {
                      setSelectedBooking(booking);
                      setBookingDialogOpen(true);
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-center min-w-[50px]">
                        <p className="text-2xl font-bold">
                          {new Date(booking.booking_date).getDate()}
                        </p>
                        <p className="text-xs font-medium uppercase">
                          {new Date(booking.booking_date).toLocaleString("default", { month: "short" })}
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold">
                          {booking.service_name || booking.installation_name || "Service Appointment"}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-sm opacity-80">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {booking.time_slot?.start_time || "TBD"} - {booking.time_slot?.end_time || "TBD"}
                          </div>
                          {booking.installation_name && (
                            <div className="flex items-center gap-1">
                              <Wrench className="h-3.5 w-3.5" />
                              {booking.installation_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <Badge className={`rounded-sm ${statusConfig[booking.status]?.color || "bg-slate-100"}`}>
                      {statusConfig[booking.status]?.label || booking.status}
                    </Badge>
                  </div>
                ))
              )}
              {calendarBookings.length > 5 && (
                <p className="text-center text-sm text-slate-500 pt-2">
                  + {calendarBookings.length - 5} more upcoming bookings
                </p>
              )}
            </div>
          ) : (
            /* Full Calendar View */
            <div>
              <div className="flex items-center justify-between mb-4">
                <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h3 className="font-semibold text-slate-900">
                  {currentDate.toLocaleString("default", { month: "long", year: "numeric" })}
                </h3>
                <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                  <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {getDaysInMonth().map((day, index) => {
                  const dayBookings = getBookingsForDay(day);
                  const hasBookings = dayBookings.length > 0;
                  return (
                    <div
                      key={`${currentDate.getFullYear()}-${currentDate.getMonth()}-${day ?? `pad-${index}`}`}
                      className={`min-h-[70px] p-1 border rounded-sm ${
                        day ? "border-slate-200" : "border-transparent"
                      } ${isToday(day) ? "bg-indigo-50 border-indigo-300" : ""} ${
                        hasBookings && !isToday(day) ? "bg-emerald-50" : ""
                      }`}
                    >
                      {day && (
                        <>
                          <span className={`text-xs font-medium ${isToday(day) ? "text-indigo-600" : "text-slate-600"}`}>
                            {day}
                          </span>
                          {hasBookings && (
                            <div className="mt-1 space-y-0.5">
                              {dayBookings.slice(0, 2).map((b) => (
                                <div
                                  key={b.booking_id}
                                  className={`text-xs truncate px-1 py-0.5 rounded cursor-pointer hover:opacity-80 ${
                                    b.status === "scheduled" ? "bg-blue-200 text-blue-800" :
                                    b.status === "confirmed" ? "bg-emerald-200 text-emerald-800" :
                                    "bg-amber-200 text-amber-800"
                                  }`}
                                  title={`${b.time_slot?.start_time || "TBD"} - ${b.service_name || b.installation_name || "Service"}`}
                                  onClick={() => {
                                    setSelectedBooking(b);
                                    setBookingDialogOpen(true);
                                  }}
                                >
                                  {b.service_name?.split(" ")[0] || "Service"}
                                </div>
                              ))}
                              {dayBookings.length > 2 && (
                                <div className="text-xs text-slate-500 pl-1">+{dayBookings.length - 2} more</div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Calendar Legend */}
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-200">
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded bg-blue-200"></div>
                  <span className="text-slate-600">Scheduled</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded bg-emerald-200"></div>
                  <span className="text-slate-600">Confirmed</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded bg-amber-200"></div>
                  <span className="text-slate-600">Pending</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Your Installations</p>
                <p className="text-3xl font-semibold text-slate-900 mt-1">
                  {installations.length}
                </p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <Cog className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Upcoming Bookings</p>
                <p className="text-3xl font-semibold text-slate-900 mt-1">
                  {upcomingBookings.length}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <Calendar className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Needs Service</p>
                <p className="text-3xl font-semibold text-slate-900 mt-1">
                  {needsService.length}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert for overdue service */}
      {needsService.length > 0 && (
        <Card className="rounded-sm border-amber-200 bg-amber-50 mb-6">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">
                  {needsService.length} installation{needsService.length > 1 ? "s" : ""} due for
                  service
                </p>
                <p className="text-sm text-amber-600">
                  Schedule a service appointment to keep your systems running smoothly
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/book-service")}
              className="rounded-sm bg-amber-600 hover:bg-amber-700"
              data-testid="book-service-alert-btn"
            >
              <CalendarPlus className="h-4 w-4 mr-2" />
              Book Service
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Installations */}
        <Card className="rounded-sm border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Your Installations</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/book-service")}
              className="rounded-sm"
              data-testid="book-service-btn"
            >
              <CalendarPlus className="h-4 w-4 mr-2" />
              Book Service
            </Button>
          </CardHeader>
          <CardContent>
            {installations.length > 0 ? (
              <div className="space-y-4">
                {installations.map((inst) => {
                  const status = getServiceStatus(inst);
                  const StatusIcon = status.icon;
                  return (
                    <div
                      key={inst.installation_id}
                      className="p-4 border border-slate-200 rounded-sm hover:bg-slate-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-indigo-100 rounded-sm flex items-center justify-center flex-shrink-0">
                            <Cog className="h-5 w-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {inst.system_name}
                            </p>
                            {inst.location_name && (
                              <div className="flex items-center gap-1 text-sm text-slate-500">
                                <MapPin className="h-3 w-3" />
                                {inst.location_name}
                              </div>
                            )}
                            {inst.next_service_due && (
                              <p className="text-sm text-slate-500 mt-1">
                                Next service: {inst.next_service_due}
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge className={`rounded-sm ${status.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {status.label}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Cog className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No installations yet</p>
                <p className="text-sm text-slate-400 mt-1">
                  Contact us to add your installations
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent/Upcoming Bookings */}
        <Card className="rounded-sm border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Upcoming Bookings</CardTitle>
          </CardHeader>
          <CardContent>
            {upcomingBookings.length > 0 ? (
              <div className="space-y-4">
                {upcomingBookings.slice(0, 5).map((booking) => (
                  <div
                    key={booking.booking_id}
                    className="p-4 border border-slate-200 rounded-sm"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900">
                          {booking.installation_name}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {booking.booking_date}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {booking.time_slot.start_time} - {booking.time_slot.end_time}
                          </div>
                        </div>
                      </div>
                      <Badge
                        className={`rounded-sm ${
                          statusConfig[booking.status]?.color || "bg-slate-100"
                        }`}
                      >
                        {statusConfig[booking.status]?.label || booking.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No upcoming bookings</p>
                <Button
                  variant="outline"
                  onClick={() => navigate("/book-service")}
                  className="mt-4 rounded-sm"
                >
                  <CalendarPlus className="h-4 w-4 mr-2" />
                  Book a Service
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Booking History */}
      {bookings.length > 0 && (
        <Card className="rounded-sm border-slate-200 mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Booking History</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-200">
              {bookings.slice(0, 10).map((booking) => (
                <div key={booking.booking_id} className="p-4 hover:bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-center min-w-[50px]">
                        <p className="text-xl font-semibold text-slate-900">
                          {new Date(booking.booking_date).getDate()}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(booking.booking_date).toLocaleString("default", {
                            month: "short",
                          })}
                        </p>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {booking.installation_name}
                        </p>
                        <p className="text-sm text-slate-500">
                          {booking.time_slot.start_time} - {booking.time_slot.end_time}
                        </p>
                      </div>
                    </div>
                    <Badge
                      className={`rounded-sm ${
                        statusConfig[booking.status]?.color || "bg-slate-100"
                      }`}
                    >
                      {statusConfig[booking.status]?.label || booking.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Booking Details Dialog (Read-only for customers) */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
            <DialogDescription>
              {selectedBooking?.service_name || "Service Appointment"}
            </DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Status</span>
                <Badge className={`rounded-sm ${statusConfig[selectedBooking.status]?.color || "bg-slate-100"}`}>
                  {statusConfig[selectedBooking.status]?.label || selectedBooking.status}
                </Badge>
              </div>
              <div className="space-y-3">
                {selectedBooking.service_name && (
                  <div className="flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-slate-400" />
                    <span className="font-medium">{selectedBooking.service_name}</span>
                  </div>
                )}
                {selectedBooking.installation_name && (
                  <div className="flex items-center gap-2">
                    <Cog className="h-4 w-4 text-slate-400" />
                    <span>{selectedBooking.installation_name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <span>{selectedBooking.booking_date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span>
                    {selectedBooking.time_slot?.start_time} - {selectedBooking.time_slot?.end_time}
                  </span>
                </div>
                {selectedBooking.assigned_worker_name && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-slate-400" />
                    <span>Technician: {selectedBooking.assigned_worker_name}</span>
                  </div>
                )}
              </div>
              {selectedBooking.notes && (
                <div className="p-3 bg-slate-50 rounded-sm">
                  <p className="text-sm text-slate-600">{selectedBooking.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDashboard;
